import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Financial Management Hub - Unified financial operations interface
 * Refactored to use TabHub component for consistency with Inventory Hub
 */
import { useState } from 'react';
import styled from 'styled-components';
import { FaChartLine, FaShoppingCart, FaFileInvoiceDollar, FaDatabase, FaToggleOn, FaToggleOff, FaClipboardList, FaExclamationTriangle, FaAddressBook } from 'react-icons/fa';
import { TabHub } from '../../components/Hubs/TabHub';
import DashboardTab from './FinancialHub/Dashboard/DashboardTab';
import PurchaseManagement from './PurchaseManagement';
import MemoManagement from './MemoManagement';
import InvoiceManagement from './InvoiceManagement';
import OverdueReports from './OverdueReports';
import PhonebookTab from './FinancialHub/PhonebookTab';
import mockDataService from '../../services/mockDataService';
const FinancialHub = () => {
    const [activeTab, setActiveTab] = useState('dashboard');
    const [isMockMode, setIsMockMode] = useState(mockDataService.isMockMode());
    const [showMockInfo, setShowMockInfo] = useState(false);
    const handleToggleMockMode = () => {
        const newMode = mockDataService.toggleMockMode();
        setIsMockMode(newMode);
        window.location.reload();
    };
    const tabs = [
        {
            id: 'dashboard',
            label: 'Dashboard',
            description: 'Overview & Actions',
            icon: _jsx(FaChartLine, {}),
            component: _jsx(DashboardTab, { onNavigateToTab: (tab) => setActiveTab(tab) }),
            tourId: 'tour-dashboard-tab'
        },
        {
            id: 'phonebook',
            label: 'Phonebook',
            description: 'All Contacts',
            icon: _jsx(FaAddressBook, {}),
            component: _jsx(PhonebookTab, {}),
            tourId: 'tour-phonebook-tab'
        },
        {
            id: 'purchases',
            label: 'Purchases',
            description: 'Orders & Payments',
            icon: _jsx(FaShoppingCart, {}),
            component: _jsx(PurchaseManagement, {}),
            tourId: 'tour-purchases-tab'
        },
        {
            id: 'memos',
            label: 'Memos',
            description: 'Memo Requests',
            icon: _jsx(FaClipboardList, {}),
            component: _jsx(MemoManagement, {}),
            tourId: 'tour-memos-tab'
        },
        {
            id: 'invoices',
            label: 'Invoices',
            description: 'Bill Customers',
            icon: _jsx(FaFileInvoiceDollar, {}),
            component: _jsx(InvoiceManagement, {}),
            tourId: 'tour-invoices-tab'
        },
        {
            id: 'overdue',
            label: 'Overdue',
            description: 'A/R & A/P Reports',
            icon: _jsx(FaExclamationTriangle, {}),
            component: _jsx(OverdueReports, {}),
            tourId: 'tour-overdue-tab'
        }
    ];
    const mockToggle = (_jsxs(MockModeToggle, { id: "tour-mock-toggle", onClick: handleToggleMockMode, active: isMockMode, children: [isMockMode ? _jsx(FaToggleOn, {}) : _jsx(FaToggleOff, {}), _jsx("span", { children: isMockMode ? 'Mock Data ON' : 'Mock Data OFF' }), _jsx(FaDatabase, {})] }));
    const infoBanner = isMockMode && showMockInfo ? (_jsxs(MockInfoBanner, { children: [_jsxs(MockInfoHeader, { children: [_jsx(MockInfoTitle, { children: "Mock Data Demo Mode" }), _jsx(CloseButton, { onClick: () => setShowMockInfo(false), children: "\u00D7" })] }), _jsxs(MockInfoContent, { children: [_jsxs(MockInfoSection, { children: [_jsx("strong", { children: "What's Included:" }), _jsxs("ul", { children: [_jsx("li", { children: "12 Realistic Suppliers with full contact details" }), _jsx("li", { children: "60 Purchase Orders over 60-day timeline" }), _jsx("li", { children: "50 Supplier Payments (some with multiple partials)" }), _jsx("li", { children: "15 Customer Payments with A/R tracking" }), _jsx("li", { children: "31 Memo Requests with complete workflow states" }), _jsx("li", { children: "20 Customer Invoices linked to real memos" }), _jsx("li", { children: "Overdue Reports with realistic aging distribution" }), _jsx("li", { children: "188 total records with complete relational integrity" })] })] }), _jsxs(MockInfoSection, { children: [_jsx("strong", { children: "Fully Functional & Linked:" }), _jsxs("ul", { children: [_jsx("li", { children: "Create, Read, Update, Delete operations" }), _jsx("li", { children: "Filters and search work perfectly" }), _jsx("li", { children: "Payment tracking syncs with purchases (cascade updates)" }), _jsx("li", { children: "Invoices link to real memos (relational integrity)" }), _jsx("li", { children: "Multiple partial payments (30% have 2-3 payments)" }), _jsx("li", { children: "COD auto-payments, edge cases (bounced, cancelled)" }), _jsx("li", { children: "Overdue reports filter from same data source" }), _jsx("li", { children: "All calculations are real-time" })] })] }), _jsxs(MockInfoSection, { children: [_jsx("strong", { children: "Toggle Off to return to real data" }), " - Your changes in mock mode are stored locally and won't affect production data."] })] })] })) : (isMockMode ? (_jsx(MockModeBadge, { onClick: () => setShowMockInfo(!showMockInfo), children: "Demo Mode Active - Click for info" })) : null);
    return (_jsx(TabHub, { title: "Financial Management Hub", subtitle: "Manage invoices, suppliers, purchases, and payments - all in one place", activeTab: activeTab, tabs: tabs, onTabChange: (tabId) => setActiveTab(tabId), headerActions: mockToggle, infoBanner: infoBanner }));
};
// Styled Components (Financial Hub specific)
const MockModeToggle = styled.button `
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: ${props => props.active ? 'rgba(16, 185, 129, 0.15)' : 'var(--bg-tertiary)'};
  color: ${props => props.active ? '#10b981' : 'var(--text-secondary)'};
  border: 1px solid ${props => props.active ? '#10b981' : 'var(--border-color)'};
  padding: 0.75rem 1.25rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  svg {
    font-size: 1.25rem;
  }

  &:hover {
    background: ${props => props.active ? 'rgba(16, 185, 129, 0.25)' : 'var(--bg-secondary)'};
  }

  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.8rem;
  }
`;
const MockModeBadge = styled.div `
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid #10b981;
  color: #10b981;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 1rem;
  text-align: center;

  &:hover {
    background: rgba(16, 185, 129, 0.2);
  }
`;
const MockInfoBanner = styled.div `
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(5, 150, 105, 0.1));
  border: 2px solid #10b981;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
`;
const MockInfoHeader = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;
const MockInfoTitle = styled.h3 `
  margin: 0;
  color: #059669;
  font-size: 1.25rem;
  font-weight: 700;
`;
const CloseButton = styled.button `
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #059669;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(16, 185, 129, 0.2);
    border-radius: 50%;
  }
`;
const MockInfoContent = styled.div `
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;
const MockInfoSection = styled.div `
  color: #047857;
  font-size: 0.875rem;
  line-height: 1.6;

  strong {
    display: block;
    margin-bottom: 0.5rem;
    color: #065f46;
    font-size: 1rem;
  }

  ul {
    margin: 0;
    padding-left: 1.25rem;
  }

  li {
    margin-bottom: 0.25rem;
  }
`;
export default FinancialHub;
